import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { Create, Toolbar, useNotify, useRefresh } from "react-admin";
import { useTranslate } from "react-admin";

import { useErrorHandler } from "../hooks/useErrorHandler";
import { useImageUploadHandler } from "../hooks/useImageUploadHandler";
import { useOrganizationContext } from "../hooks/useOrganizationContext";
import { color } from "../theme/designTokens";
import { BaseModalProps } from "../types/modal";
import { CloseButton, SaveButton } from "./buttons";
import { PatientForm, PatientFormData } from "./PatientForm";

const PatientCreateToolbar = ({ onCancel }: { onCancel: () => void }) => {
  return (
    <Toolbar>
      <SaveButton />
      <CloseButton onClick={onCancel} />
    </Toolbar>
  );
};

const PatientCreate = ({ open, onClose }: BaseModalProps) => {
  const { organizationId } = useOrganizationContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const handleError = useErrorHandler();
  const translate = useTranslate();

  const { handleImageUpload } = useImageUploadHandler<PatientFormData>();

  const handleTransform = (data: PatientFormData) => {
    if (!organizationId) {
      throw new Error(translate("create.noOrganizationSelected"));
    }

    const dataWithOrganization = {
      ...data,
      organizationId,
      locationId: data.locationId || null,
    };

    return handleImageUpload(dataWithOrganization);
  };

  const handleSuccess = () => {
    notify("create.patient.success", { type: "success" });
    refresh();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle sx={{ backgroundColor: color.background }}>{translate("create.patient.title")}</DialogTitle>
      <DialogContent>
        <Create
          resource="Patient"
          transform={handleTransform}
          record={{ organizationId }}
          mutationOptions={{
            onError: error => handleError(error, translate("create.patient.error")),
            onSuccess: handleSuccess,
          }}
        >
          <PatientForm toolbar={<PatientCreateToolbar onCancel={onClose} />} />
        </Create>
      </DialogContent>
    </Dialog>
  );
};

export default PatientCreate;
